import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';

import { useBudgetActions } from '#budget';
import type { ApplyBudgetActionPayload } from '#budget';
import { Boton } from '#components/mobile/ui/Boton';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { HojaAcciones } from '#components/mobile/ui/HojaAcciones';
import type { FilaAccion } from '#components/mobile/ui/HojaAcciones';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { useCategories } from '#hooks/useCategories';
import { useCategory } from '#hooks/useCategory';
import { useFormat } from '#hooks/useFormat';
import { useUndo } from '#hooks/useUndo';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';

import { useGuardarObjetivo } from './ObjetivoPage';
import { esObjetivoConFecha } from './recuerdos';
import type { Recuerdo, SalidaTravesia } from './recuerdos';
import { destinosRumbo, movimientoRumbo } from './travesias';
import { useAccionesRecuerdo, useRecuerdos } from './useRecuerdos';
import { useTravesiaCategoria } from './useTravesiaCategoria';

type HojaCerrarTravesiaProps = {
  categoryId: string;
  month: string;
  onCerrar: () => void;
};

async function saldoActual(categoryId: string): Promise<number> {
  try {
    const celda = await send('get-cell', {
      sheetName: monthUtils.sheetForMonth(monthUtils.currentMonth()),
      name: `leftover-${categoryId}`,
    });
    return typeof celda?.value === 'number' ? celda.value : 0;
  } catch {
    return 0;
  }
}

/**
 * «Cerrar la travesía» (F1 §1.8): tres salidas sin juicio. «Devolver la
 * botella al mar» borra la carta; «Cambiar de rumbo» pasa la carta a otra
 * travesía y mueve lo ahorrado (como «Mover», con aviso y «Deshacer»);
 * «Archivar» la guarda en el cajón. En las tres, la categoría deja de tener
 * meta con fecha (vuelve a ser normal); el dinero se queda donde está salvo
 * al cambiar de rumbo.
 */
export function HojaCerrarTravesia({
  categoryId,
  month,
  onCerrar,
}: HojaCerrarTravesiaProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const format = useFormat();
  const { data: category } = useCategory(categoryId);
  const { data: { list: categorias } = { list: [] } } = useCategories();
  const { objetivo, recuerdo } = useTravesiaCategoria(category);
  const recuerdos = useRecuerdos();
  const { cerrarTravesia, puedeEscribir } = useAccionesRecuerdo();
  const { guardar } = useGuardarObjetivo(categoryId, month);
  const applyBudgetAction = useBudgetActions();
  const { showUndoNotification } = useUndo();
  const [paso, setPaso] = useState<'elegir' | 'devolver' | 'rumbo'>('elegir');
  const [ocupado, setOcupado] = useState(false);

  const nombre = category ? separarEmoji(category.name) : null;
  const destinos = recuerdo ? destinosRumbo(recuerdos, recuerdo) : [];
  const nombreDestino = (r: Recuerdo) =>
    categorias.find(c => c.id === r.base.categoriaId && !c.tombstone)?.name ??
    [r.base.emoji, r.base.nombre].filter(Boolean).join(' ');

  const avisar = (mensaje: string) =>
    dispatch(
      addNotification({
        notification: { type: 'message', message: mensaje, timeout: 4000 },
      }),
    );

  const salir = async (salida: SalidaTravesia, destino?: Recuerdo) => {
    if (ocupado || !category) {
      return;
    }
    setOcupado(true);
    try {
      if (recuerdo && !(await cerrarTravesia(recuerdo.base.rid, salida))) {
        avisar(t('The voyage could not be closed. Try again.'));
        return;
      }
      if (esObjetivoConFecha(objetivo)) {
        await guardar([], '');
      }
      if (salida.tipo === 'rumbo' && destino) {
        const mov = movimientoRumbo({
          saldo: await saldoActual(category.id),
          desde: category.id,
          hacia: destino.base.categoriaId,
        });
        const hacia = nombreDestino(destino);
        if (mov) {
          applyBudgetAction.mutate({
            month: monthUtils.currentMonth(),
            type: 'transfer-category',
            args: { ...mov, currencyCode: format.currency.code },
          } as ApplyBudgetActionPayload);
          showUndoNotification({
            message: t(
              'The letter and {{amount}} moved to {{categoryName}}. Undo only moves the money back.',
              {
                amount: format(mov.amount, 'financial'),
                categoryName: hacia,
              },
            ),
          });
        } else {
          avisar(
            t('The letter moved to {{categoryName}}.', {
              categoryName: hacia,
            }),
          );
        }
      } else if (salida.tipo === 'devolver') {
        avisar(t('The bottle went back to the sea.'));
      } else {
        avisar(t('Voyage archived.'));
      }
      onCerrar();
    } finally {
      setOcupado(false);
    }
  };

  const titulo = t('Close the voyage');
  const subtitulo = nombre?.resto ?? '';

  if (paso === 'devolver') {
    return (
      <HojaAcciones
        titulo={titulo}
        emoji={nombre?.emoji}
        subtitulo={subtitulo}
        filas={[]}
        onCerrar={onCerrar}
        data-testid="hoja-cerrar-travesia"
      >
        <View style={{ gap: 12, paddingTop: 4 }}>
          <Text style={{ ...texto.cuerpo, color: color.fg }}>
            <Trans>
              The letter will be deleted and leave no trace. The money stays in
              the category.
            </Trans>
          </Text>
          <Boton
            bloque
            variante="peligro"
            isDisabled={ocupado}
            data-testid="cerrar-devolver-confirmar"
            onPress={() => void salir({ tipo: 'devolver' })}
          >
            <Trans>Return the bottle to the sea</Trans>
          </Boton>
          <Boton bloque variante="fantasma" onPress={() => setPaso('elegir')}>
            <Trans>Back</Trans>
          </Boton>
        </View>
      </HojaAcciones>
    );
  }

  if (paso === 'rumbo') {
    return (
      <HojaAcciones
        titulo={t('Change course')}
        emoji={nombre?.emoji}
        subtitulo={t('Choose the voyage that takes the letter and the money')}
        filas={[]}
        onCerrar={onCerrar}
        data-testid="hoja-cerrar-travesia"
      >
        <View style={{ gap: 4, paddingTop: 4 }}>
          {destinos.map(d => (
            <Button
              key={d.base.rid}
              variant="bare"
              isDisabled={ocupado}
              data-testid={`rumbo-a-${d.base.categoriaId}`}
              onPress={() =>
                void salir({ tipo: 'rumbo', destino: d.base.rid }, d)
              }
              style={{
                minHeight: Math.max(TACTIL, 52),
                justifyContent: 'flex-start',
                padding: '6px 12px',
                borderRadius: radio.sm,
                backgroundColor: color.surface2,
                color: color.fg,
                textAlign: 'left',
                ...texto.fila,
                overflowWrap: 'anywhere',
              }}
            >
              {nombreDestino(d)}
            </Button>
          ))}
          <Boton
            bloque
            variante="fantasma"
            onPress={() => setPaso('elegir')}
            style={{ marginTop: 8 }}
          >
            <Trans>Back</Trans>
          </Boton>
        </View>
      </HojaAcciones>
    );
  }

  const sinDestinos = destinos.length === 0;
  const filas: FilaAccion[] = [
    {
      id: 'cerrar-devolver',
      texto: t('Return the bottle to the sea'),
      detalle: t('The letter is deleted. No trace is left.'),
      icono: 'x',
      deshabilitada: !puedeEscribir || ocupado,
      onPress: () => setPaso('devolver'),
    },
    {
      id: 'cerrar-rumbo',
      texto: t('Change course'),
      detalle: sinDestinos
        ? t('There is no other voyage under way yet.')
        : t('The letter and the savings go to another voyage.'),
      icono: 'compass',
      deshabilitada: sinDestinos || !puedeEscribir || ocupado,
      onPress: () => setPaso('rumbo'),
    },
    {
      id: 'cerrar-archivar',
      texto: t('Archive'),
      detalle: t('The letter is kept in Archived, out of sight.'),
      icono: 'note',
      deshabilitada: !puedeEscribir || ocupado,
      onPress: () => void salir({ tipo: 'archivar' }),
    },
  ];
  return (
    <HojaAcciones
      titulo={titulo}
      emoji={nombre?.emoji}
      subtitulo={subtitulo}
      filas={filas}
      onCerrar={onCerrar}
      data-testid="hoja-cerrar-travesia"
    />
  );
}
